import { useCallback, useEffect } from 'react';
import { usePartnerDataClient } from '../api/use-partner-data-client';
import { DEFAULT_PAGE_SIZE } from '../constants/partner-data-gateway.constant';
import { FileRecordPreview, PaginatedResult } from '../domain/types';
import { useAsyncResource } from './use-async-resource';
import { useCursorPagination } from './use-cursor-pagination';

export interface UseIngestionRecordsParams {
  /**
   * Required by `pdgIngestionRecords` — it forces the query onto the
   * `(event_id, batch_id)` index. An empty value means "no batch chosen
   * yet" and skips the request rather than sending an invalid one.
   */
  batchId: string;
  limit?: number;
}

export interface UseIngestionRecordsReturn {
  records: FileRecordPreview[];
  isLoading: boolean;
  hasError: boolean;
  hasNext: boolean;
  hasPrevious: boolean;
  currentPage: number;
  goToNext: () => void;
  goToPrevious: () => void;
  refetch: () => void;
}

const EMPTY_RECORDS_PAGE: PaginatedResult<FileRecordPreview> = {
  items: [],
  pageInfo: { hasNextPage: false, nextCursor: null },
};

/**
 * A page of raw parsed rows for one batch, with the cursor stack the module
 * uses everywhere else (see `use-cursor-pagination.ts`) so the caller can
 * page forward and backward without knowing how cursors are transported.
 *
 * The payload is PHI and the BFF guards the query with `GqlPhiGuard`, so a
 * user without PHI access gets a GraphQL error — surfaced here as `hasError`
 * for the caller to render, never as a crash.
 */
export const useIngestionRecords = (
  eventId: string,
  { batchId, limit = DEFAULT_PAGE_SIZE }: UseIngestionRecordsParams,
): UseIngestionRecordsReturn => {
  const client = usePartnerDataClient();
  const { cursor, hasPrevious, currentPage, goToNext, goToPrevious, reset } =
    useCursorPagination();

  const fetchRecords = useCallback(
    () =>
      batchId
        ? client.listRecords(eventId, { batchId, cursor, limit })
        : Promise.resolve(EMPTY_RECORDS_PAGE),
    [client, eventId, batchId, cursor, limit],
  );

  const { data, isLoading, hasError, refetch } = useAsyncResource(
    fetchRecords,
    EMPTY_RECORDS_PAGE,
  );

  // Switching batches starts a new result set, so the cursor stack from the
  // previous batch must not carry over.
  useEffect(() => {
    reset();
  }, [eventId, batchId, limit, reset]);

  return {
    records: data.items,
    isLoading,
    hasError,
    hasNext: data.pageInfo.hasNextPage,
    hasPrevious,
    currentPage,
    goToNext: (): void => goToNext(data.pageInfo),
    goToPrevious,
    refetch,
  };
};
